import {
  ComponentMultiStyleConfig,
  FormControl,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { CMFormInputBaseDropDown, DropdownOption } from "../../../control";
import { CMButtonFormCommentProps } from "../../comment";
import { CMFormControlHeader } from "../CMFormControlHeader/CMFormControlHeader";
import {
  ChangeBadgeProperties,
  messageId,
  ValueObjectMetaEnum,
} from "../../../../lib";
import { useMessageDictionary } from "../../../../hooks/use-message-dictionary";
import { FormRequiredLabelProps } from "../../label";
import { hasValue, isNullish } from "../../../../lib/util/common-util";

export type CMFormInputDropDownProps<T extends [string, ...string[]]> = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";

  /**
   * trueの場合、ヘッダなしとなる。
   * この場合、changeBadgePropertiesやcommentButtonPropsが存在しても、変更やコメントを扱わない。
   */
  noHeader?: boolean;

  label?: string;
  placeholder?: string;
  /**
   * これがtrueの場合のみ、未選択状態(null)を選択可能とする。
   * デフォルト: false
   */
  nullable?: boolean;

  /**
   * 値。
   */
  value?: T[number] | null;

  /**
   * 読み取り専用モードのみ使用
   * 表示内容をリンク表示するために使用
   */
  url?: string;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMeta: ValueObjectMetaEnum<T>;

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   */
  changeBadgeProperties?: ChangeBadgeProperties<T[number]>;

  /**
   * コメントボタンのprops。
   * 定義されている場合、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  onChange?: (selectedValue: T[number] | null) => void;

  // このコンポーネントではバリデーションを行わないため、onValidateは提供しない。
} & ComponentStyleProps;

/**
 * Dropdownコンポーネント
 */
export const CMFormInputDropDown = <T extends [string, ...string[]]>({
  editMode = "editable",
  size = "md",

  noHeader = false,

  label,
  placeholder,
  nullable,

  value = null,
  url,

  valueObjectMeta,

  requiredLabelProps,

  changeBadgeProperties,

  commentButtonProps,

  onChange,

  sx,
  ...rest
}: CMFormInputDropDownProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputDropDownStyles");
  const dict = useMessageDictionary();

  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );

  const options: DropdownOption<T[number]>[] = useMemo(
    () =>
      valueObjectMeta.values.map((v) => ({
        label: dict(messageId(valueObjectMeta.dict[v])) ?? "",
        value: v,
      })),
    [valueObjectMeta, dict],
  );

  // const hasChangedInDiffMode: boolean | undefined = useMemo(() => {
  //   if (
  //     isNullish(changeBadgeProperties) ||
  //     isNullish(changeBadgeProperties.changedFrom)
  //   )
  //     return undefined;
  //
  //   return value !== changeBadgeProperties.changedFrom;
  // }, [value, changeBadgeProperties]);

  const handleChange = useCallback(
    (value: string | null) => {
      if (isNullish(value)) {
        onChange?.(null);
        return;
      }

      const result = valueObjectMeta.validate(value);
      if (result.state === "ok") {
        onChange?.(result.value);
      } else {
        onChange?.(null);
      }
    },
    [onChange, valueObjectMeta],
  );

  const toDisplayValue = useCallback(
    (value: T[number]) => {
      const option = options.find((o) => o.value === value);
      return option?.label ?? "";
    },
    [options],
  );

  return (
    <FormControl sx={{ ...styles.container, ...sx }} {...rest}>
      {!noHeader && (
        <CMFormControlHeader<string>
          label={label}
          value={value}
          requiredLabelProps={requiredLabelProps}
          changeBadgeProperties={changeBadgeProperties}
          toDisplayValue={toDisplayValue}
          commentButtonProps={commentButtonProps}
          sx={styles.header}
        />
      )}
      <CMFormInputBaseDropDown<T[number]>
        editMode={editMode}
        variant={isDiffMode ? "diff" : "default"}
        size={size}
        placeholder={placeholder ?? dict(valueObjectMeta.placeholder)}
        nullable={nullable}
        options={options}
        value={value}
        url={url}
        changedFrom={changeBadgeProperties?.changedFrom}
        onChange={handleChange}
        sx={styles.input}
      />
    </FormControl>
  );
};

export const CMFormInputDropDownStyles: ComponentMultiStyleConfig = {
  parts: ["container", "header", "input", "error"],
  baseStyle: {
    container: {},
    header: {
      mb: "2px",
    },
    input: {},
    error: {
      mt: "4px",
    },
  },
};
